import { type IGeoreference } from "@interfaces/common/IGeoreference";
import { type ITypeState } from "@interfaces/common/ITypeState";
import { type IAccessibleModule } from "@interfaces/modules/IAccessibleModule";
import { type IPlanStatus } from "@interfaces/modules/IGetModulesResponse";
import { StatePersistenceService } from "@services/state-persistence.service";
import {
  hasValidOptionalProperty,
  isNullableString,
  isRecord,
} from "@utils/state-validation.utils";

import { computed, effect, inject, Service, signal } from "@angular/core";

export interface CommonStateModel {
  typeStates: ITypeState[];
  currentTenant: string | null;
  currentTenantBusinessName: string | null;
  savedRut: string | null;
  accessibleModules: IAccessibleModule[];
  planStatus: IPlanStatus | null;
  companyGeoreference: IGeoreference | null;
}

const DEFAULT_STATE: CommonStateModel = {
  typeStates: [],
  currentTenant: null,
  currentTenantBusinessName: null,
  savedRut: null,
  accessibleModules: [],
  planStatus: null,
  companyGeoreference: null,
};

function isPersistedCommonState(value: unknown) {
  return (
    isRecord(value) &&
    hasValidOptionalProperty(value, "typeStates", Array.isArray) &&
    hasValidOptionalProperty(value, "currentTenant", isNullableString) &&
    hasValidOptionalProperty(
      value,
      "currentTenantBusinessName",
      isNullableString
    ) &&
    hasValidOptionalProperty(value, "savedRut", isNullableString) &&
    hasValidOptionalProperty(value, "accessibleModules", Array.isArray) &&
    hasValidOptionalProperty(
      value,
      "planStatus",
      status => status === null || isRecord(status)
    ) &&
    hasValidOptionalProperty(
      value,
      "companyGeoreference",
      georeference => georeference === null || isRecord(georeference)
    )
  );
}

@Service()
export class CommonStore {
  private readonly persistence = inject(StatePersistenceService);
  private state = signal(this.hydrateState());

  typeStates = computed(() => this.state().typeStates);
  currentTenant = computed(() => this.state().currentTenant);
  currentTenantBusinessName = computed(
    () => this.state().currentTenantBusinessName
  );
  savedRut = computed(() => this.state().savedRut);
  accessibleModules = computed(() => this.state().accessibleModules);
  accessibleModuleNames = computed(
    () => new Set(this.state().accessibleModules.map(module => module.name))
  );
  planStatus = computed(() => this.state().planStatus);
  companyGeoreference = computed(() => this.state().companyGeoreference);

  constructor() {
    effect(() => this.persistence.write("Common", this.state()));
  }

  getTypeStateByDescription(description: string) {
    return this.state().typeStates.find(
      typeState => typeState.description === description
    );
  }

  setTypeState(typeStates: ITypeState[]) {
    this.state.update(state => ({ ...state, typeStates }));
  }

  setCurrentTenant(currentTenant: string | null) {
    const tenantChanged = this.currentTenant() !== currentTenant;
    this.state.update(state =>
      !tenantChanged
        ? { ...state, currentTenant }
        : {
            ...state,
            currentTenant,
            currentTenantBusinessName: null,
            accessibleModules: [],
            planStatus: null,
            companyGeoreference: null,
          }
    );
  }

  setSavedRut(savedRut: string) {
    this.state.update(state => ({ ...state, savedRut }));
  }

  clearSavedRut() {
    this.state.update(state => ({ ...state, savedRut: null }));
  }

  setCurrentTenantBusinessName(currentTenantBusinessName: string | null) {
    this.state.update(state => ({ ...state, currentTenantBusinessName }));
    this.persistence.write("Common", this.state());
  }

  setAccessibleModules(accessibleModules: IAccessibleModule[]) {
    this.state.update(state => ({ ...state, accessibleModules }));
  }

  clearAccessibleModules() {
    this.state.update(state => ({ ...state, accessibleModules: [] }));
  }

  setPlanStatus(planStatus: IPlanStatus | null) {
    this.state.update(state => ({ ...state, planStatus }));
  }

  setCompanyGeoreference(companyGeoreference: IGeoreference) {
    this.state.update(state => ({ ...state, companyGeoreference }));
  }

  clearCompanyGeoreference() {
    this.state.update(state => ({ ...state, companyGeoreference: null }));
  }

  private hydrateState() {
    const persisted = this.persistence.read<Partial<CommonStateModel>>(
      "Common",
      {},
      isPersistedCommonState
    );
    return { ...DEFAULT_STATE, ...persisted };
  }
}
